package fyi.moko.hotpl.data.network

import kotlinx.serialization.KSerializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

/**
 * `Long` for the fields that go into a Firestore document — timestamps and version counters.
 *
 * ── 왜 기본 Long 직렬화를 못 쓰는가 ──────────────────────────────
 * Kotlin/JS에서 `Long`은 JS 숫자가 아니라 두 개의 32비트 정수로 흉내 낸 **박싱된 클래스**다.
 * GitLive가 그 객체를 그대로 넘기면 Firestore JS SDK가 이렇게 거부한다:
 *
 *     INVALID_ARGUMENT: Function setDoc() called with invalid data.
 *     Unsupported field value: a custom Long object (found in field createdAt ...)
 *
 * 로컬 우선 구조라 화면에는 아무 증상이 없고 `sync_queue`만 조용히 재시도한다. 그래서
 * **웹에서 만든 장소·방문이 원격에 한 번도 도달한 적이 없다는 사실이 2026-09-07까지
 * 드러나지 않았다**(§14 0-15 조사 중 발견). Android/iOS는 진짜 64비트 정수라 무사했다.
 *
 * ── 왜 Double로 보내면 되는가 ──────────────────────────────
 * Firestore Web SDK는 JS 숫자를 받을 때 `Number.isSafeInteger`면 **integerValue로** 저장한다
 * (아니면 doubleValue). 배포된 규칙이 `createdAt is int`·`updatedAt is int`를 요구하므로
 * 이 구분이 핵심이다 — 정수로 저장되지 않으면 이번엔 규칙에 걸린다. 밀리초 타임스탬프(~1.8e12)와
 * 버전 카운터는 2^53(~9.0e15)에서 한참 아래라 정확히 표현된다.
 *
 * Android/iOS는 손대지 않는다. 그쪽 인코더는 `Long`을 그대로 정수로 쓰고 있고, 굳이 Double을
 * 거치면 왕복 경로만 하나 더 는다.
 *
 * **HTTP JSON DTO에는 쓰지 말 것** — Google Places·Nominatim 응답은 Firestore를 안 거치므로
 * 기본 `Long`이 맞다.
 */
object FirestoreLong : KSerializer<Long> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("fyi.moko.hotpl.data.network.FirestoreLong", PrimitiveKind.LONG)

    override fun serialize(encoder: Encoder, value: Long) = encodeFirestoreLong(encoder, value)

    override fun deserialize(decoder: Decoder): Long = decodeFirestoreLong(decoder)
}

/** Writes [value] in whatever shape this platform's Firestore encoder stores as an integer. */
internal expect fun encodeFirestoreLong(encoder: Encoder, value: Long)

/** Reads back what [encodeFirestoreLong] wrote on this platform. */
internal expect fun decodeFirestoreLong(decoder: Decoder): Long
